import Fuse from 'fuse.js';
import { notEmpty } from './util/array-util';

export interface Successable<T> {
  onSuccess(t: T): void;
  onFailure(): void;
}

export type VForm = Vue & {
  validate: () => boolean;
  resetValidation: () => boolean;
  reset: () => void;
};

export type UUID = string

export interface Dim {
  readonly width: number;
  readonly height: number;
}
export interface Pos {
  readonly x: number;
  readonly y: number;
}

const locale = 'sv-SE';
export type Time = number;
export const Time = {
  asString(time: Time, seconds?: boolean): string {
    return new Date(time).toLocaleTimeString(locale,
      {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: seconds ? 'numeric' : undefined,
      });
  },
  asDateString(time: Time): string {
    return new Date(time).toLocaleDateString(locale);
  },
  parse(time: string): Time {
    return Date.parse(time);
  },
  parseYear(year: number): Time {
    return Date.parse(`${year}`);
  },
  now(): Time {
    return Date.now();
  },
  year(time: Time): number {
    return new Date(time).getFullYear();
  },
};

export interface Interval {
  from: number;
  to: number;
}

export interface Session {
  readonly userId: number;
  readonly authenticated: boolean;
  readonly email: string;
  readonly givenName: string;
  readonly familyName: string;
}
export interface OIDCConfig {
  readonly url: string;
  readonly realm: string;
  readonly clientId: string;
  readonly idpHint: string;
  readonly requiredRole: string;
}

export type SerfJobType = 'crawl' | 'ingest' | 'translate' | 'suggest' | 'bulk_summary' | 'ner' | 'describe';
export type SerfJobStatus = 'pending' | 'started' | 'failed' | 'complete' | 'cancelled';
export interface SerfJobAndManifest {
  readonly job: SerfJob;
  readonly manifest: SerfJobManifest;
}
export interface SerfJob {
  readonly id: number;
  readonly manifestId: number;
  readonly type: SerfJobType;
  readonly status: SerfJobStatus;
  readonly created: Time;
  readonly started: Time;
  readonly stopped: Time;
  readonly serfName: string;
  readonly priority: number;
}
export interface SerfJobManifest {
  readonly id: number;
  readonly type: SerfJobType;
  readonly documents?: readonly FileDocument[];
  readonly options?: object;
}

export type FileDocumentStatus = 'pending' | 'analysis' | 'qa' | 'completed' | 'summary';
export const FileDocumentStatus = {
  name(status: FileDocumentStatus) {
    return this.options.find((o) => o.value === status)?.name;
  },
  options: [
    { name: 'Pending', value: 'pending', ordinal: 0 },
    { name: 'Analysis', value: 'analysis', ordinal: 1 },
    { name: 'QA', value: 'qa', ordinal: 2 },
    { name: 'Pending Summary', value: 'summary', ordinal: 4 },
    { name: 'Completed', value: 'completed', ordinal: 3 },
  ] as readonly { readonly name: string; readonly value: FileDocumentStatus; readonly ordinal: number }[],
};

export type FileDocumentLicense = 'red' | 'yellow' | 'green';
export const fileDocumentLicenseOptions: readonly FileDocumentLicense[] = ['red', 'yellow', 'green'];

export type DocumentPublishedFormat = 'year' | 'date';

export interface FileDocumentStatusHistory {
  status: FileDocumentStatus;
  time: Time;
  userId?: number;
  documentId: number;
}

export function formatPublished(time: Time | undefined, format: DocumentPublishedFormat = 'year'): string | undefined {
  if (!time) {
    return undefined;
  }
  return format === 'date' ? Time.asDateString(time) : `${Time.year(time)}`;
}

export function fileDocumentName(document: Partial<FileDocument>, publishedFormat: DocumentPublishedFormat = 'year'): string {
  const publisher = document.publisher?.name;
  const geo = document.publisher?.geography;
  const published = formatPublished(document.published, publishedFormat);
  const { name } = document;
  const reference = [publisher, published ? `(${published})` : undefined, name, geo]
    .filter(notEmpty)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join('. ')
    .trim();

  return reference.length > 0 ? `${reference}.` : '';
}
export type Language = 'en' | 'sv' | 'no' | 'da' | 'fr';
export const languageOptions: readonly Language[] = ['en', 'sv', 'no', 'da', 'fr'];

export interface FileDocument {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly url: string;
  readonly language: Language;
  readonly published: Time;
  readonly subjectYear: number;
  readonly userId: number;
  readonly file?: FileDescriptor;
  readonly publisher: Publisher;
  readonly realmId: number;
  readonly status?: FileDocumentStatus;
  readonly license: FileDocumentLicense;
  readonly labels: readonly number[];
}

export interface FileDescriptor {
  readonly id: number;
  readonly uuid: string;
  readonly name: string;
  readonly mime: string;
  readonly persisted: Time;
  readonly hash: string;
  readonly length: number;
}

export interface IngestBox {
  readonly id: number;
  readonly pageId: number;
  readonly seqNo: number;
  readonly width: number;
  readonly height: number;
  readonly x: number;
  readonly y: number;
  readonly text?: string;
  readonly file?: UUID;
  readonly fontSize?: number;
  readonly wordMargin?: number;
  readonly lineHeight?: number;
  readonly rotation?: number;
  readonly previousBoxId?: number;
  readonly type?: string;
  readonly hierarchyRef?: string;
  readonly hierarchyId?: number;
}
export interface IngestPage {
  readonly id: number;
  readonly ingestId: number;
  readonly seqNo: number;
  readonly width: number;
  readonly height: number;
  readonly thumbnail: string;
  readonly boxes: readonly IngestBox[];
}
export interface IngestDocument {
  readonly id: number;
  readonly documentId: number;
  readonly jobId?: number;
  readonly translated?: Language;
  readonly meta?: Record<string, string>;
  readonly hierarchies?: readonly IngestHierarchy[];
  readonly pages: readonly IngestPage[];
}

export interface IngestHierarchy {
  readonly id: number;
  readonly ingestId: number;
  readonly source: string;
  readonly ref: string;
  readonly parentRef?: string;
  readonly parentId?: number;
  readonly type?: string;
  readonly boxIds: readonly number[];
}

export interface IngestDescriptor {
  readonly ingestId?: number;
  readonly job?: SerfJob;
}
export interface IngestResult {
  readonly descriptor: IngestDescriptor;
  readonly document: FileDocument;
  readonly ingest: IngestDocument;
  readonly data: readonly FileDescriptor[];
}

export interface Color {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export interface LabelEdge {
  readonly fromLabelId: number;
  readonly toLabelId: number;
}

export interface LabelType {
  readonly code: string;
  readonly color: Color;
  readonly selectable: boolean;
  readonly sortOrder?: number;
  readonly created?: Time;
  readonly updated?: Time;
}

export interface Label {
  readonly id: number;
  readonly type: string;
  readonly code: string;
  readonly color: Color;
  readonly description?: string;
  readonly externalId?: string;
  readonly selectable: boolean;
  readonly created?: Time;
  readonly updated?: Time;
}

export interface LabelFilter {
  readonly types?: readonly string[];
  readonly search?: string;
  readonly selectableOnly?: boolean;
  readonly hasExternalId?: boolean;
}

export type LabelWithEdges = Label & { edges?: Label[] }

export interface LabelState {
  readonly isDocumentLabel?: boolean;
  readonly isIntersectingLabel?: boolean;

  readonly isRecentlyUsed?: boolean;
  readonly isPinned?: boolean;
  readonly isRemoved?: boolean;
  readonly isAdded?: boolean;
  readonly isSelected?: boolean;

  readonly relatedTo?: ReadonlyArray<LabelRelatedTo>;
  readonly isParentAtIndex?: number;
  readonly warning?: string;

  readonly fuseScore?: number;
  readonly fuseMatch?: Fuse.FuseResultMatch;
}

export type LabelRelatedTo = {label: Label; index: number; isRemoved?: boolean};

export type LabelWithStateAndSuggestions = LabelWithEdges & LabelStateAndSuggestions;

export type LabelWithSuggestions = LabelWithEdges & LabelSuggestions;

export type LabelStateAndSuggestions = LabelState & LabelSuggestions & {
  readonly id: number;
}

export type LabelSuggestions = {
  readonly id: number;
  readonly suggestions?: readonly LabelSuggestion[];
}
export interface IngestFragment {
  readonly id: number;
  readonly type: 'text' | 'image';
  readonly description?: string;
  readonly color?: Color;
  readonly labels: readonly LabelAssignment[];
  readonly removedLabels?: readonly LabelAssignment[];
  readonly isolate: boolean;
  readonly created?: Time;
}

export type LabelSuggestionType = 'potential' | 'ingest' | 'outofcontext' | 'ml' | 'related';

export type LabelSuggestion = {
  type: LabelSuggestionType;
}
export type LabelSuggestionWithId = LabelSuggestion & {
  readonly id: number;
}

export interface PotentialSuggestion extends LabelSuggestion {
  readonly type: 'potential';
  readonly confidence: number;
  readonly fromLabelIds: ReadonlyArray<ReadonlyArray<number>>;
}

export const isPotentialSuggestion = (
  suggestion: LabelSuggestion,
): suggestion is PotentialSuggestion => suggestion.type === 'potential';

export interface MlSuggestion extends LabelSuggestion {
  readonly type: 'ml';
  readonly confidence: number;
}

export const isMlSuggestion = (
  suggestion: LabelSuggestion,
): suggestion is MlSuggestion => suggestion.type === 'ml';

export type Glasses = { group: string; value: Label; isActive: boolean };

export type LabelPickerAdvancedLabelStatus = {
  saved: readonly LabelAssignment[];
  added: readonly LabelAssignment[];
  removed: readonly LabelAssignment[];
  recentlyUsed?: readonly number[];
  pinned?: readonly number[];
  parents: readonly LabelWithEdges[];
};

export interface LabelAssignment {
  readonly labelId: number;
  readonly userId?: number;
  readonly time?: Time;
  readonly deletedBy?: number;
  readonly deleted?: Time;
  readonly suggestions?: readonly LabelSuggestion[];
}

export type IngestFragmentBox = Interval & {
  readonly boxId: number;
  readonly seqNo: number | undefined;
}

export type IngestTextFragment = IngestFragment & {
  readonly type: 'text';
  readonly boxes: readonly IngestFragmentBox[];
  readonly correction?: string;
}

export function isIngestTextFragment(f: IngestFragment): f is IngestTextFragment {
  return f.type === 'text';
}

export type ScoreDoc = {
  readonly score: number;
  readonly doc: number;
  readonly shardIndex: number;
}

export type SearchResult = {
  readonly name: string;
  readonly fileId?: number;
  readonly ingestId?: number;
  readonly boxId?: number;
  readonly fragmentId?: number;
  readonly fragments?: readonly string[];
  readonly highlights?: readonly string[];
}

export type ExpandedFragmentBox = {
  readonly pageId: number;
  readonly boxId: number;
  readonly page: number;
  readonly text?: string;
  readonly previousBoxId?: number;
  readonly intervals: IngestFragmentBox[];
  readonly media?: FileDescriptor;
}

export type ExpandedFragment = {
  readonly fragment: IngestFragment;
  readonly documentId: number;
  readonly ingestId: number;
  readonly boxes: readonly ExpandedFragmentBox[];
  readonly labels: readonly number[];
  readonly documentLabels: readonly number[];
  readonly intersectingLabels: readonly number[];
  readonly intersectingIntervals: readonly IngestFragmentBox[];
}

export type Clause = StructuralClause | ConditionalClause
export interface StructuralClause {
  readonly type: 'and' | 'or';
  readonly negate?: boolean;
  readonly clauses: readonly Clause[];
}
export interface ConditionalClause {
  readonly type: 'in';
  readonly field: SelectionField;
  readonly values: readonly number[];
  readonly negate?: boolean;
}

export interface SelectionQuery {
  readonly clause: Clause;
  readonly realms?: number[];
  readonly page?: number;
  readonly documentFilter?: DocumentFilter;
}

export type SelectionField = 'label' | 'fragment' | 'document' | 'ingestion' | 'page'
  | 'box' | 'assignedUser' | 'publisher' | 'status' | 'addedBy' | 'deletedBy';

export interface SelectionResultSliceComment {
  readonly fragmentId: number;
  readonly text: string;
}

export interface SelectionResultGroup {
  readonly fragmentIds: readonly number[];
  readonly labels: readonly number[];
  readonly isolate: boolean;
}

export interface SelectionResultSlice {
  readonly boxId: number;
  readonly from: number;
  readonly to: number;
  readonly labels: readonly number[];
  readonly fragments: readonly number[];
  readonly groups: readonly SelectionResultGroup[];
  readonly comments: readonly SelectionResultSliceComment[];
}
export interface SelectionResultBox {
  readonly boxId: number;
  readonly seqNo: number;
  readonly text: string;
  readonly previousBoxId?: number;
  readonly slices: readonly SelectionResultSlice[];
}
export interface SelectionResultPage {
  readonly pageId: number;
  readonly seqNo: number;
  readonly boxes: readonly SelectionResultBox[];
}
export interface SelectionResultDoc {
  readonly ingestId: number;
  readonly document: FileDocument;
  readonly translated?: Language;
  readonly pages: readonly SelectionResultPage[];
  readonly totalPageCount: number;
}
export interface SelectionResult {
  readonly docs: readonly SelectionResultDoc[];
}
export interface SavedQuery {
  readonly id: number;
  readonly name: string;
  readonly time: Time;
  readonly shared: boolean;
  readonly userId: number;
  readonly clause: Clause;
}
export interface User {
  readonly id: number;
  readonly uuid: UUID;
  readonly email: string;
  readonly givenName: string;
  readonly familyName: string;
  readonly displayName: string;
}
export interface Suggestion {
  readonly id: number;
  readonly ingest: number;
  readonly boxId: number;
  readonly labelId: number;
  readonly jobId: number;
  readonly from: number;
  readonly to: number;
  readonly confidence?: number;
  readonly action?: SuggestionAction;
}
export interface SuggestionAction {
  readonly userId?: number;
  readonly fragmentId?: number;
  readonly created?: Time;
}

export interface EntityAttributeOption {
  readonly id: number;
  readonly entityAttributeId: number;
  readonly key: string;
  readonly value?: string;
  readonly displayName?: string;
  readonly labelId?: number;
}

export interface EntityAttribute {
  readonly id: number;
  readonly entityTypeId: number;
  readonly attributeType: string;
  readonly valueType: string;
  readonly description?: string;
  readonly options: EntityAttributeOption[];
  readonly labelId?: number;
}

export interface EntityType {
  readonly id: number;
  readonly name: string;
  readonly description?: string;
  readonly attributes: EntityAttribute[];
  readonly labelId?: number;
}

export interface ExtractedEntityAttribute {
  readonly id: number;
  readonly extractedEntityId: number;
  readonly entityAttributeId: number;
  readonly raw?: string;
  readonly normalized?: string;
  readonly entityAttributeOptionId?: number;
  readonly value?: string;
}

export interface ExtractedEntitySpan {
  readonly id?: number;
  readonly extractedEntityId: number;
  readonly boxId?: number;
  readonly iFrom?: number;
  readonly iTo?: number;
}

export interface ExtractedEntity {
  readonly id: number;
  readonly entityTypeId: number;
  readonly rawText?: string;
  readonly documentId?: number;
  readonly pageId?: number;
  readonly spans: ExtractedEntitySpan[];
  readonly attributes: ExtractedEntityAttribute[];
}

export interface Publisher {
  readonly id: number;
  readonly name: string;
  readonly geography?: string;
  readonly shortName?: string;
  readonly license?: FileDocumentLicense;
}

export interface Realm {
  readonly id: number;
  readonly name: string;
  readonly dfault?: boolean;
}

export interface PotentialLabelContext {
  readonly implicit?: number[];
  readonly explicit?: number[];
  readonly confidence?: number;
  readonly subsetRules?: boolean;
  readonly maxRuleLength?: number;
}

export interface PotentialLabelResultItem {
  readonly from: readonly number[];
  readonly to: readonly number[];
  readonly lift: number;
  readonly confidence: number;
}

export interface MlSuggestContext {
  readonly text: string;
  readonly pageContext?: string;
  readonly topK?: number;
}

export interface MlThemePrediction {
  readonly labelId: number;
  readonly score: number;
  readonly active: boolean;
}

export interface MlLabelSuggestionItem {
  readonly labelId: number;
  readonly type: string;
  readonly code: string;
  readonly score: number;
}

export interface MlLabelSuggestions {
  readonly themes: readonly MlThemePrediction[];
  readonly suggestions: readonly MlLabelSuggestionItem[];
}

export interface DocumentFilter {
  readonly documentIds?: readonly number[];
  readonly published?: Time;
  readonly labels?: readonly number[];
  readonly realms?: readonly number[];
  readonly publishers?: readonly number[];
  readonly users?: readonly number[];
  readonly statuses?: readonly FileDocumentStatus[];
  readonly languages?: readonly Language[];
  readonly subjectYear?: number;
  readonly from?: Time;
  readonly to?: Time;
  readonly search?: string;
  readonly limit?: number;
  readonly page?: number;
  readonly licenses?: readonly FileDocumentLicense[];
  readonly statusHistory?: StatusHistoryFilter;
}
export interface StatusHistoryFilter {
  readonly from?: Time;
  readonly to?: Time;
  readonly statuses?: readonly FileDocumentStatus[];
}

export interface SummarySpec {
  readonly members: number[][];
  readonly include?: number[];
}

export interface SummarySpecWithInstructions {
  readonly spec: SummarySpec;
  readonly instructions: string;
}
export interface BulkSummarySpecWithInstructions {
  readonly specs: SummarySpec[];
  readonly instructions: string;
  readonly autoApprove?: boolean;
}

export interface BulkSummaryProgress {
  readonly isRunning: boolean;
  readonly status: SerfJobStatus;
}

export interface SummarySlice {
  readonly id: number;
  readonly summaryId: number;
  readonly documentId: number;
  readonly ingestId: number;
  readonly pageId: number;
  readonly pageNo: number;
  readonly boxId: number;
  readonly from: number;
  readonly to: number;
  readonly fragmentIds: readonly number[];
  readonly seqNo: number;
}
export interface Summary {
  readonly id: number;
  readonly text: string;
  readonly correction?: string;
  readonly created: Time;
  readonly createdBy: number;
  readonly edited?: Time;
  readonly editedBy?: number;
  readonly slices: readonly SummarySlice[];
  readonly selectionLabelIds: readonly number[];
  readonly motivation?: string;
  readonly externalRequestId?: number;
}

export interface DocumentSummaryApproval {
  readonly id: number;
  readonly time: Time;
  readonly userId: number;
  readonly approved: boolean;
}
export interface DocumentSummary {
  readonly id: number;
  readonly documentId: number;
  readonly summary: Summary;
  readonly approvals: readonly DocumentSummaryApproval[];
  readonly head: boolean;
}

export interface SummaryGroup {
  readonly selectionLabelIds: readonly number[];
  readonly head?: DocumentSummary;
  readonly tail?: DocumentSummary[];
  readonly specSlices?: readonly SummarySlice[];
}

export interface DocumentSummaries {
  readonly document: FileDocument;
  readonly groups: Array<SummaryGroup>;
}

export interface LabelIdentifier {
  readonly labelId: number;
  readonly score: number;
  readonly tf: number;
  readonly freq: number;
  readonly total: number;
  readonly idf: Idf;
}

export interface Idf {
  readonly freq: number;
  readonly total: number;
  readonly idf: number;
}

export interface ChatMessage {
  readonly role: 'user' | 'system' | 'assistant';
  readonly content: string;
}
export interface SemanticSearchResult {
  readonly item: {
    readonly id: number;
    readonly text: string;
    readonly score: number;
  }
  readonly fragments: IngestTextFragment[];
  readonly document: FileDocument;
}
//    public record FilterMetadata(String field, List<Long> values, String negate) {}

export interface MetaFilter {
  readonly field: string;
  readonly values: readonly number[];
  readonly negate?: boolean;
}

export interface SemanticSearchRequest {
  readonly query: string;
  readonly limit?: number;
  readonly filters?: MetaFilter[];
}

export interface UiMenuItem {
  readonly title: string;
  readonly icon?: string;
  readonly route?: string;
  readonly children?: readonly UiMenuItem[];
}

export interface UiGlassesItem {
  readonly type: string;
  readonly icon: string;
}

export interface UiLabelPickerConfig {
  readonly useGlasses?: boolean;
  readonly useContext?: boolean;
  readonly usePotential?: boolean;
  readonly documentResultPinnedTypes?: string[];
  readonly defaultActiveFilters?: string[];
  readonly alwaysInContext?: string[];
  readonly glasses?: UiGlassesItem[];
  readonly contextRelationTypes?: string[];
  readonly excludeTypes?: string[];
  readonly contextTypes?: string[];
  readonly includeTypes?: string[];
  readonly mlSuggestionPools?: { readonly [theme: string]: readonly string[] };
  readonly mlAlwaysVisibleTypes?: string[];
  readonly autoAddOnType?: { readonly [triggerType: string]: UiAutoAddSpec };
}

export interface UiAutoAddSpec {
  readonly add?: readonly string[];
  readonly suggest?: readonly string[];
}

export interface UiDocumentsConfig {
  readonly publishedFormat?: DocumentPublishedFormat;
  readonly hiddenStatuses?: FileDocumentStatus[];
  readonly documentLabelTypes?: string[];
  readonly defaultPersonalView?: boolean;
}

export interface UiConfig {
  readonly menu?: { readonly items?: UiMenuItem[] };
  readonly documents?: UiDocumentsConfig;
  readonly labelPicker?: UiLabelPickerConfig;
  readonly features?: { readonly summaries?: boolean };
}
